/**
 * Estate completion validator — surfaces server-side closure checks and POST /close.
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../utils/api';
import { API_BASE_URL } from '../../config/api';

export interface CompletionChecks {
  allPhasesComplete: boolean;
  debtsSettled: boolean;
  assetsDistributed: boolean;
  financialReconciliationZero: boolean;
  beneficiaryApprovals: boolean;
  courtDischargeUploaded: boolean;
  balanceCents: number;
  ledgerEntryCount: number;
}

export interface EstateCompletionValidatorProps {
  estateId: string | null;
  readOnly?: boolean;
  /** Called after a successful close so layout context can refresh archive state */
  onClosed?: () => void | Promise<void>;
}

export default function EstateCompletionValidator({ estateId, readOnly, onClosed }: EstateCompletionValidatorProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [checks, setChecks] = useState<CompletionChecks | null>(null);
  const [missing, setMissing] = useState<string[]>([]);
  const [canClose, setCanClose] = useState(false);
  const [isArchived, setIsArchived] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!estateId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr(null);
    try {
      const res = await apiService.request(`/api/settlement/${estateId}/completion-status`, { method: 'GET' });
      if (!res.success) {
        setErr(res.error || 'Unable to load completion status');
        setLoading(false);
        return;
      }
      setChecks(res.checks || null);
      setMissing(Array.isArray(res.missing) ? res.missing : []);
      setCanClose(!!res.canClose);
      setIsArchived(!!res.isArchived);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [estateId]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleClose = async () => {
    if (!estateId || readOnly || !canClose || isArchived) return;
    setBusy(true);
    setErr(null);
    try {
      const token = localStorage.getItem('account-token');
      const response = await fetch(`${API_BASE_URL}/api/settlement/${estateId}/close`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      const res = await response.json();
      if (!response.ok) {
        setErr(res.error || 'Close request failed');
        if (Array.isArray(res.missing)) setMissing(res.missing);
        setBusy(false);
        return;
      }
      await onClosed?.();
      await load();
      navigate(estateId ? `/sframe-259/${estateId}` : '/sframe-259');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to close estate');
    } finally {
      setBusy(false);
    }
  };

  if (!estateId) {
    return <p className="text-sm text-slate-600">Select an estate to validate closure.</p>;
  }

  if (loading) {
    return <p className="text-sm text-slate-500">Loading validation…</p>;
  }

  const rows: { key: keyof CompletionChecks; label: string }[] = [
    { key: 'allPhasesComplete', label: 'All pre-close phases complete' },
    { key: 'debtsSettled', label: 'All debt & tax steps complete' },
    { key: 'assetsDistributed', label: 'All distribution steps complete' },
    { key: 'financialReconciliationZero', label: 'Financial reconciliation ($0 + confirmation)' },
    { key: 'beneficiaryApprovals', label: 'Beneficiary approvals step marked complete' },
    { key: 'courtDischargeUploaded', label: 'Court discharge / order uploaded (close step 3)' },
  ];

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-slate-900">Estate completion validator</h3>
        <p className="text-sm text-slate-600 mt-1">
          The server blocks premature closure until every requirement below passes. Archived estates stay read-only.
        </p>
      </div>

      {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">{err}</div>}

      {isArchived && (
        <div className="text-sm text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md p-3">
          This estate is already settled and archived.
        </div>
      )}

      <ul className="divide-y divide-slate-100 border border-slate-100 rounded-lg overflow-hidden">
        {rows.map((r) => {
          const ok = checks ? !!checks[r.key] : false;
          return (
            <li key={r.key} className="flex items-center justify-between gap-3 px-4 py-3 bg-white">
              <span className="text-sm text-slate-800">{r.label}</span>
              <span className={`text-xs font-semibold px-2 py-1 rounded-full ${ok ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-900'}`}>
                {ok ? 'Pass' : 'Incomplete'}
              </span>
            </li>
          );
        })}
      </ul>

      {!canClose && missing.length > 0 && (
        <div>
          <p className="text-sm font-medium text-slate-800 mb-2">Outstanding items</p>
          <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
            {missing.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        </div>
      )}

      {!readOnly && !isArchived && (
        <div className="flex flex-wrap gap-3 items-center">
          <button
            type="button"
            disabled={!canClose || busy}
            onClick={() => void handleClose()}
            className="rounded-md bg-slate-900 text-white text-sm font-medium px-4 py-2 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-800"
          >
            {busy ? 'Closing…' : 'Settle & archive estate'}
          </button>
          <button type="button" onClick={() => void load()} className="text-sm text-slate-600 underline">
            Refresh status
          </button>
        </div>
      )}
    </div>
  );
}
